import { useMemo } from 'react';
import { FileText, Check, User, Home, Heart, CreditCard, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Separator } from '~/components/ui/separator';
import { StepHeader } from '~/components/forms/StepHeader';
import { AcknowledgmentsChunk } from '~/components/form-chunks/AcknowledgmentsChunk';

/**
 * ReviewStep - Summary before submission with acknowledgments.
 * Delegates to AcknowledgmentsChunk for the acknowledgment checkboxes.
 */
export function ReviewStep({ formData, updateFormData }: any) {
  const acknowledgments = formData?.acknowledgments || {};

  const summary = useMemo(() => {
    const returningStudents = (formData?.returningStudents || [])
      .filter((s: any) => s.isReturning !== false);

    const notReturningStudents = (formData?.returningStudents || [])
      .filter((s: any) => s.isReturning === false);

    // New student structure: persisted + unpersisted - removed
    const persistedNewStudentsToRemove = formData?.persistedNewStudentsToRemove || [];
    const persistedNewStudents = (formData?.persistedNewStudents || [])
      .filter((s: any) => !persistedNewStudentsToRemove.includes(s.studentEnrollmentId))
      .filter((s: any) => s.firstName || s.lastName);
    const unpersistedNewStudents = (formData?.unpersistedNewStudents || [])
      .filter((s: any) => s.firstName || s.lastName);

    // Combine all new students for display
    const newStudents = [
      ...persistedNewStudents.map((s: any) => ({ ...s, isPersisted: true })),
      ...unpersistedNewStudents.map((s: any) => ({ ...s, isPersisted: false }))
    ];

    // Students being removed
    const studentsBeingRemoved = (formData?.persistedNewStudents || [])
      .filter((s: any) => persistedNewStudentsToRemove.includes(s.studentEnrollmentId));

    const familyInfo = formData?.familyInfo || {};
    const address = familyInfo.address || {};
    const tuition = formData?.tuitionPreferences || {};

    // Get available options from the frozen snapshot embedded in the form
    const availableOptions = formData?.availableOptions || {};
    const tuitionOptions = availableOptions.tuitionOptions || [];
    const paymentOptions = availableOptions.paymentOptions || [];

    return {
      returningStudents,
      notReturningStudents,
      newStudents,
      studentsBeingRemoved,
      familyInfo,
      address,
      tuition,
      tuitionOptions,
      paymentOptions,
      totalStudents: returningStudents.length + newStudents.length
    };
  }, [formData]);

  const handleAcknowledgmentsUpdate = (updatedAcknowledgments: any) => {
    updateFormData('acknowledgments', updatedAcknowledgments);
  };

  const canSubmit = acknowledgments.infoAccurate && acknowledgments.understandPolicy;

  return (
    <div className="space-y-6">
      <StepHeader
        icon={FileText}
        title="Review & Submit"
        description="Please review your information before submitting your re-enrollment."
      />

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Students Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-5 w-5" />
              Students
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {summary.returningStudents.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Returning</p>
                <div className="space-y-2">
                  {summary.returningStudents.map((student: any) => (
                    <div key={student.studentEnrollmentId} className="flex items-center justify-between">
                      <span>{student.firstName} {student.lastName}</span>
                      <Badge variant="outline">
                        {student.nextGradeLevel || student.currentGradeLevel || 'Grade TBD'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {summary.newStudents.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">New Students</p>
                <div className="space-y-2">
                  {summary.newStudents.map((student: any) => (
                    <div key={student.studentEnrollmentId || student.tempId} className="flex items-center justify-between">
                      <span>{student.firstName} {student.lastName}</span>
                      <Badge variant="secondary">
                        {student.gradeLevel || 'Grade TBD'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {summary.studentsBeingRemoved?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Being Removed</p>
                <div className="space-y-2">
                  {summary.studentsBeingRemoved.map((student: any) => (
                    <div key={student.studentEnrollmentId} className="flex items-center justify-between text-muted-foreground">
                      <span className="line-through">{student.firstName} {student.lastName}</span>
                      <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">Removing</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {summary.notReturningStudents.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Not Returning</p>
                <div className="space-y-2">
                  {summary.notReturningStudents.map((student: any) => (
                    <div key={student.studentEnrollmentId} className="flex items-center justify-between text-muted-foreground">
                      <span>{student.firstName} {student.lastName}</span>
                      <Badge variant="outline" className="bg-muted">Not Returning</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />
            <p className="text-sm">
              <span className="font-medium">{summary.totalStudents}</span> student{summary.totalStudents !== 1 ? 's' : ''} enrolling for next year
            </p>
          </CardContent>
        </Card>

        {/* Address Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Home className="h-5 w-5" />
              Address
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summary.address.street ? (
              <p className="text-sm">
                {summary.address.street}
                {summary.address.street2 && <><br />{summary.address.street2}</>}
                {(summary.address.city || summary.address.state || summary.address.zip) && (
                  <><br />{summary.address.city}, {summary.address.state} {summary.address.zip}</>
                )}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">No address provided</p>
            )}
          </CardContent>
        </Card>

        {/* Medical Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Medical Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-green-500" />
              <span>Medical information provided for {summary.totalStudents} student{summary.totalStudents !== 1 ? 's' : ''}</span>
            </div>
          </CardContent>
        </Card>

        {/* Tuition Summary */}
        {(() => {
          const enrollingStudentIds = [
            ...summary.returningStudents.map((s: any) => s.studentEnrollmentId),
            ...summary.newStudents.map((s: any) => s.studentEnrollmentId || s.tempId)
          ];
          const studentSelections = summary.tuition?.studentSelections || {};
          const familyPaymentOptionKey = summary.tuition?.paymentOptionKey;

          const allStudentsHaveTuition = enrollingStudentIds.length > 0 && enrollingStudentIds.every((id: string) => {
            const sel = studentSelections[id];
            return sel?.tuitionOptionKey;
          });
          const hasPaymentOption = !!familyPaymentOptionKey;
          const allComplete = allStudentsHaveTuition && hasPaymentOption;

          return (
            <Card className={!allComplete ? 'border-amber-300' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Tuition Preferences
                  {!allComplete && (
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {!allComplete ? (
                  <p className="text-sm text-amber-600 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {!allStudentsHaveTuition
                      ? 'Please go back and select a tuition option for all enrolling students'
                      : 'Please go back and select a payment plan'}
                  </p>
                ) : (
                  <>
                    {enrollingStudentIds.map((studentId: string) => {
                      const selection = studentSelections[studentId];
                      if (!selection?.tuitionOptionKey) return null;

                      const student = summary.returningStudents.find((s: any) => s.studentEnrollmentId === studentId) ||
                                     summary.newStudents.find((s: any) => (s.studentEnrollmentId || s.tempId) === studentId);
                      const studentName = student ? `${student.firstName} ${student.lastName}` : 'Student';

                      const tuitionOption = summary.tuitionOptions.find((t: any) => t.key === selection.tuitionOptionKey);
                      const tuitionName = tuitionOption?.name || selection.tuitionOptionKey;

                      return (
                        <div key={studentId} className="text-sm border-b pb-2 last:border-0 last:pb-0">
                          <p className="font-medium mb-1">{studentName}</p>
                          <p>
                            <span className="text-muted-foreground">Tuition:</span>{' '}
                            {tuitionName}
                          </p>
                        </div>
                      );
                    })}
                    <Separator className="my-2" />
                    {(() => {
                      const paymentOption = summary.paymentOptions.find((p: any) => p.key === familyPaymentOptionKey);
                      const paymentName = paymentOption?.name || familyPaymentOptionKey;
                      return (
                        <p className="text-sm">
                          <span className="font-medium">Payment Plan:</span>{' '}
                          {paymentName}
                        </p>
                      );
                    })()}
                    <p className="text-sm">
                      <span className="font-medium">Financial Assistance:</span>{' '}
                      {summary.tuition?.seekingFinancialAssistance ? 'Requested' : 'Not requested'}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Acknowledgments */}
        <AcknowledgmentsChunk
          acknowledgments={acknowledgments}
          onUpdate={handleAcknowledgmentsUpdate}
          isApplication={false}
        />

        {!canSubmit && (
          <div className="flex items-center gap-2 text-amber-600 text-sm justify-center">
            <AlertCircle className="h-4 w-4" />
            <span>Please check both acknowledgments before submitting</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewStep;
